import { useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { OrderSummary } from '../orders/domain'
import { problemTitle } from '../parts/usePartsLibrary'

/**
 * UID-300 — the data-layer seam for the Workspace detail page's Orders tab
 * AND the header stat card's `orderCount`. `OrdersListPage.tsx` calls
 * `apiClient.GET('/orders', { params: { query: { from, to } } })` directly
 * (no injectable service) — this is a thin, workspace-scoped WRAPPER around
 * that SAME `GET /orders` call (not a reimplementation), passing
 * `?workspaceId=` through and reading only the `items` half of the
 * `{ items, spend }` envelope — this tab has no spend/date-range UI, so
 * `spend` is deliberately unused here. Injectable for tests.
 *
 * NOTE (flagged, per ticket): `?workspaceId` on `GET /orders` and the
 * regenerated client are assumed to land from a parallel backend ticket —
 * see `bffListOrders`'s doc comment.
 */
export interface WorkspaceOrdersService {
  listOrders(workspaceId: string): Promise<readonly OrderSummary[]>
}

/**
 * Calls the SAME `GET /orders` endpoint `OrdersListPage` uses, with
 * `workspaceId` added to the query — assumes the generated client has been
 * regenerated against the backend contract this depends on (parallel
 * UID-300 backend subtask). Until that lands, `workspaceId` is not a valid
 * query param on the generated `operations['listOrders']` type, so this is
 * EXPECTED to be a type error, not a bug to work around with a cast.
 */
async function bffListOrders(workspaceId: string): Promise<readonly OrderSummary[]> {
  const { data, error } = await apiClient.GET('/orders', {
    params: { query: { workspaceId } },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, "We couldn't load this project's orders. Try again."))
  }
  return data.items
}

export const defaultWorkspaceOrdersService: WorkspaceOrdersService = {
  listOrders: bffListOrders,
}

export type WorkspaceOrdersState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly orders: readonly OrderSummary[] }

export interface UseWorkspaceOrdersResult {
  readonly state: WorkspaceOrdersState
}

/**
 * Loads this workspace's Order slice through a `WorkspaceOrdersService`
 * (defaults to the real BFF-backed `defaultWorkspaceOrdersService`). Called
 * unconditionally alongside `useWorkspace`/`usePartsLibrary`/
 * `useWorkspaceRfqs` in `WorkspacePage.tsx` (Rules of Hooks) — this ALSO
 * means the order count is available for the header stat card even before
 * the Orders tab is ever visited.
 */
export function useWorkspaceOrders(
  workspaceId: string,
  service: WorkspaceOrdersService = defaultWorkspaceOrdersService,
): UseWorkspaceOrdersResult {
  const [state, setState] = useState<WorkspaceOrdersState>({ status: 'loading' })
  const serviceRef = useRef(service)
  serviceRef.current = service

  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })

    serviceRef.current
      .listOrders(workspaceId)
      .then((orders) => {
        if (!cancelled) setState({ status: 'ready', orders })
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message:
              error instanceof Error
                ? error.message
                : "We couldn't load this project's orders. Try again.",
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, [workspaceId])

  return { state }
}
